import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideUser } from '@ng-icons/lucide';
import { BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import {
  DEFAULT_VISITKA_DESIGN,
  defaultVisitkaMessenger,
  VISITKA_MESSENGERS,
  visitkaContactUrl,
  type VisitkaContact,
  type VisitkaDesign,
  type VisitkaNetwork,
  type VisitkaTheme,
} from '@lite-workspace/dto-fotoclass-gallery';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';

/** Every channel the account page can hold — the site is its own field, not a channel. */
export type PhotographerChannel = Exclude<VisitkaNetwork, 'website'>;

/** One filled channel, already normalised to an absolute http(s) URL by the opener. */
export interface PhotographerCardLink {
  channel: PhotographerChannel;
  url: string;
}

/**
 * The photographer's contact block as the public screens receive it. The gate
 * store owns the fetch — this window only renders what it is handed. Every
 * field is optional: an empty one simply has no line and no button.
 */
export interface PhotographerCard {
  name: string | null;
  avatarUrl: string | null;
  /** City / genre line under the name. */
  subtitle: string | null;
  /** The ONLY contact rendered as plain text — everything else is a button. */
  phone: string | null;
  email: string | null;
  siteUrl: string | null;
  /** Filled channels; the default messenger comes first among the messengers. */
  links: readonly PhotographerCardLink[];
  /** «Светлая»/«Тёмная» из настроек аккаунта; отсутствует = тёмная. */
  theme?: VisitkaTheme;
  /** Выбранный фотографом дизайн визитки; отсутствует = `DEFAULT_VISITKA_DESIGN`. */
  design?: VisitkaDesign;
}

/**
 * Which footer the window wears. `client` — the single «Закрыть» the visitor sees; `settings` — the
 * photographer's own preview, where the same window offers «Настроить визитку» — the way to the
 * account block that owns this data; `preview` — превью из настроек НА ТЕЛЕФОНЕ: визитка занимает
 * весь экран шторкой, поэтому «Сменить дизайн» несёт она сама (на десктопе кнопку рисует обёртка
 * превью снаружи карточки, а сюда приходит клиентский футер). One component, three footers: a copy
 * would drift.
 */
export type PhotographerCardFooter = 'client' | 'settings' | 'preview';

/**
 * The window closes on `'account-settings'` when the photographer chose to go and edit the card,
 * and on `'change-design'` when he asked for the design carousel under his own preview.
 */
export type PhotographerCardResult = 'account-settings' | 'change-design' | undefined;

/** What the opener hands the window — `HlmDialogOpener` context, never an input. */
export interface PhotographerCardContext {
  card: PhotographerCard;
  footer?: PhotographerCardFooter;
}

/** «Иванова Анна» → «ИА»; first letters of the first two words, uppercase. */
export function photographerInitials(name: string | null): string {
  const words = (name ?? '').trim().split(/\s+/u).filter(Boolean).slice(0, 2);
  return words.map(word => word[0]!.toUpperCase()).join('');
}

/** A messenger says «Написать в …»; a social wears its bare brand name. */
const MESSENGERS: readonly PhotographerChannel[] = VISITKA_MESSENGERS;

/** Соцсети — в порядке карточки «Социальные сети», следом старые сети, которые ещё хранят значение. */
const CARD_SOCIALS: readonly PhotographerChannel[] = ['vk', 'youtube', 'mywed', 'instagram', 'tiktok', 'facebook', 'pinterest'];

/**
 * «Контакты» → визитка: ровно то, что увидит клиент. Одна сборка на все поверхности —
 * превью фотографа, карточка в настройках галереи и публичный экран клиента, — чтобы они
 * не разошлись. Ссылки нормализует общий `visitkaContactUrl`: он же отсекает небезопасные
 * значения. Пустое поле не даёт ни строки, ни кнопки.
 */
export function photographerCardFromContacts(
  contacts: readonly VisitkaContact[],
  who: {
    name: string | null;
    avatarUrl: string | null;
    subtitle?: string | null;
    theme?: VisitkaTheme | null;
    design?: VisitkaDesign | null;
    /** A Telegram connected through the support bot: it goes first. Public screens get the order from the server. */
    telegramConnected?: boolean;
  },
): PhotographerCard {
  const data = (name: VisitkaContact['name']): string | null => contacts.find(c => c.name === name)?.data.trim() || null;
  const link = (channel: PhotographerChannel): PhotographerCardLink | null => {
    const url = visitkaContactUrl(channel, data(channel));
    return url ? { channel, url } : null;
  };

  const filled = (channels: readonly PhotographerChannel[]): PhotographerCardLink[] =>
    channels.map(link).filter((l): l is PhotographerCardLink => l !== null);

  // Мессенджер по умолчанию не фильтр, а порядок: показываем все заполненные, его — первым.
  const first = defaultVisitkaMessenger(contacts, who.telegramConnected === true);
  const messengers = filled(MESSENGERS).sort((a, b) => Number(b.channel === first) - Number(a.channel === first));

  return {
    name: who.name,
    avatarUrl: who.avatarUrl,
    // «Описание фотографа» живёт строкой в тех же контактах, поэтому превью фотографа
    // берёт его отсюда; публичные экраны присылают его отдельным полем визитки.
    subtitle: who.subtitle ?? data('description'),
    phone: data('phone'),
    email: data('email'),
    siteUrl: visitkaContactUrl('website', data('website')),
    links: [...filled(CARD_SOCIALS), ...messengers],
    // Тема живёт той же строкой контактов; публичные экраны присылают её отдельным
    // полем визитки. Всё, кроме явной «light», — тёмная визитка.
    theme: (who.theme ?? data('visitkaTheme')) === 'light' ? 'light' : 'dark',
    // Дизайн — той же дорогой: публичные экраны присылают его полем визитки,
    // настройки читают ту же строку контактов. Пусто = дизайн по умолчанию.
    design: who.design ?? (data('visitkaDesign') as VisitkaDesign | null) ?? undefined,
  };
}

const CHANNEL_NAMES: Record<PhotographerChannel, string> = {
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  max: 'Max',
  vk: 'VK',
  youtube: 'YouTube',
  mywed: 'Mywed',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  pinterest: 'Pinterest',
};

/**
 * Photographer's business card — one window shared by the entry screen, the gallery
 * (spec «Галерея клиента» 43) and the photographer's own preview in the account settings.
 * In the spirit of a link-in-bio page: avatar, name, phone as text, and every other contact
 * as a full-width button. No round icon row — a glyph alone never says where it leads.
 */
@Component({
  selector: 'public-photographer-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, HlmButton, DialogSheetComponent, BrnDialogTitle],
  providers: [provideIcons({ lucideUser })],
  host: {
    '[class]': 'hostClass',
    // Тема — это DS-класс на хосте: `.dark` перекрашивает те же токены, дизайн
    // визитки стилит обе стороны по этому же классу.
    '[class.dark]': "theme === 'dark'",
    '[class.light]': "theme === 'light'",
    // Дизайн — второй класс на хосте; пять блоков стилей ниже читают только его.
    '[class.design-a]': "design() === 'a'",
    '[class.design-b]': "design() === 'b'",
    '[class.design-c]': "design() === 'c'",
    '[class.design-d]': "design() === 'd'",
    '[class.design-e]': "design() === 'e'",
  },
  // Визитка монохромная в обе стороны: фиолетовый акцент DS здесь переводится
  // на чернила, как это уже сделано в публичной галерее.
  styles: `
    :host(.dark),
    :host(.light) {
      --acc: var(--ink);
      --acc-2: var(--ink);
      --acc-soft: var(--ink-12);
      --acc-line: var(--ink-40);
      --sel: var(--ink);
      --on-acc: var(--panel);
      box-shadow: var(--shadow-3);
    }

    /* A — сплошные «таблетки» чернилами: белые на тёмной визитке, чёрные на светлой. */
    :host(.design-a) .actions [hlmBtn] {
      height: calc(var(--spacing) * 13.5);
      border: 0;
      border-radius: var(--r-pill);
      background: var(--ink);
      color: var(--panel);
      font-weight: var(--font-weight-semibold);
    }
    :host(.design-a) .actions [hlmBtn]:hover {
      background: var(--ink-plate-hover);
      transform: translateY(calc(var(--spacing) * -0.25));
    }

    /* B — плоские строки на подложке с волосяной рамкой. */
    :host(.design-b) .actions {
      gap: calc(var(--spacing) * 2.5);
    }
    :host(.design-b) .actions [hlmBtn] {
      height: calc(var(--spacing) * 13);
      border-radius: var(--r-l);
      background: var(--bg2);
      color: var(--ink);
      border: var(--border-width-hairline) solid var(--ink-18);
      font-weight: var(--font-weight-medium);
    }
    :host(.design-b) .actions [hlmBtn]:hover {
      background: var(--panel-2);
      border-color: var(--ink-55);
    }

    /* C — приподнятые светлые строки: подложка светлая в обеих темах, поэтому
       она набрана токенами светлой поверхности, а не перекрашиваемым --panel. */
    :host(.design-c) .actions {
      gap: calc(var(--spacing) * 3.5);
    }
    :host(.design-c) .actions [hlmBtn] {
      height: calc(var(--spacing) * 14);
      border: 0;
      border-radius: var(--r-l);
      background: var(--surface-light);
      color: var(--ink-on-light);
      box-shadow: var(--shadow-2);
      font-weight: var(--font-weight-semibold);
    }
    :host(.design-c) .actions [hlmBtn]:hover {
      background: var(--surface-light);
      transform: translateY(calc(var(--spacing) * -0.5));
      box-shadow: var(--shadow-3);
    }

    /* D — журнальный: имя прописными, список на волосяных линейках, «Закрыть» ссылкой. */
    :host(.design-d) h2 {
      font-size: var(--text-base);
      font-weight: var(--font-weight-medium);
      letter-spacing: var(--ls-caps-3xl);
      text-transform: uppercase;
    }
    :host(.design-d) h2::after {
      content: '';
      display: block;
      width: calc(var(--spacing) * 13.5);
      height: var(--border-width-hairline);
      margin: calc(var(--spacing) * 3.5) auto 0;
      background: var(--ink-14);
    }
    :host(.design-d) .avatar {
      background: transparent;
      border: var(--border-width-hairline) solid var(--ink-30);
      color: var(--ink-85);
      font-weight: normal;
      letter-spacing: var(--ls-caps-l);
    }
    :host(.design-d) .actions {
      gap: 0;
      border-top: var(--border-width-hairline) solid var(--ink-14);
    }
    :host(.design-d) .actions [hlmBtn] {
      height: calc(var(--spacing) * 14);
      border: 0;
      border-bottom: var(--border-width-hairline) solid var(--ink-14);
      border-radius: 0;
      background: transparent;
      color: var(--ink-82);
      font-size: var(--text-xs);
      font-weight: var(--font-weight-medium);
      letter-spacing: var(--ls-caps-2xl);
      text-transform: uppercase;
      transition:
        color var(--t-fast) var(--ease-smooth),
        background var(--t-fast) var(--ease-smooth),
        letter-spacing var(--t-mid) var(--ease-smooth);
    }
    :host(.design-d) .actions [hlmBtn]:hover {
      background: var(--ink-05);
      color: var(--ink);
      letter-spacing: var(--ls-caps-4xl);
    }
    :host(.design-d) .actions button[hlmBtn]:last-child {
      height: auto;
      margin-top: calc(var(--spacing) * 6);
      border: 0;
      background: transparent;
      color: var(--c3);
      font-size: var(--text-fine);
      letter-spacing: var(--ls-caps-2xl);
    }
    :host(.design-d) .actions button[hlmBtn]:last-child:hover {
      background: transparent;
      color: var(--ink);
      letter-spacing: var(--ls-caps-2xl);
    }

    /* E — стеклянные строки поверх размытой подложки. */
    :host(.design-e) {
      position: relative;
      overflow: hidden;
      isolation: isolate;
      background: transparent;
    }
    :host(.design-e)::before {
      content: '';
      position: absolute;
      inset: calc(var(--spacing) * -10);
      z-index: -1;
      background:
        radial-gradient(50% 40% at 26% 18%, var(--panel-3), transparent 60%),
        radial-gradient(60% 50% at 40% 86%, var(--bg), transparent 70%),
        linear-gradient(160deg, var(--panel-2) 0%, var(--bg) 55%, var(--bg2) 100%);
      filter: blur(calc(var(--spacing) * 6.5));
    }
    :host(.design-e) .actions {
      gap: calc(var(--spacing) * 2.5);
    }
    :host(.design-e) .actions [hlmBtn] {
      height: calc(var(--spacing) * 13.5);
      border-radius: var(--r-l);
      background: var(--glass-wash);
      border: var(--border-width-hairline) solid var(--glass-line);
      backdrop-filter: blur(calc(var(--spacing) * 3.5));
      -webkit-backdrop-filter: blur(calc(var(--spacing) * 3.5));
      color: var(--ink);
      font-weight: var(--font-weight-medium);
    }
    :host(.design-e) .actions [hlmBtn]:hover {
      background: var(--ink-12);
      border-color: var(--ink-35);
    }
  `,
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <div class="flex flex-col items-center gap-6">
          <div class="flex flex-col items-center gap-3 text-center">
            @if (card.avatarUrl; as avatar) {
              <img [src]="avatar" alt="" class="avatar size-20 rounded-full object-cover" />
            } @else if (initials) {
              <span class="avatar bg-acc-soft text-ink grid size-20 place-items-center rounded-full text-2xl font-medium">{{
                initials
              }}</span>
            } @else {
              <span class="avatar bg-acc-soft text-c2 grid size-20 place-items-center rounded-full">
                <ng-icon name="lucideUser" size="32" aria-hidden="true" />
              </span>
            }
            <h2 brnDialogTitle class="text-ink text-xl font-medium">{{ name }}</h2>
            @if (card.subtitle; as sub) {
              <p class="text-c2 text-sm leading-5">{{ sub }}</p>
            }
            @if (phone) {
              <p class="text-c2 text-sm leading-5">{{ phone }}</p>
            }
          </div>

          <div class="actions flex w-full flex-col gap-3">
            @for (action of actions; track action.href) {
              <a
                hlmBtn
                variant="outline"
                class="w-full pointer-coarse:min-h-11"
                [href]="action.href"
                [attr.target]="action.external ? '_blank' : null"
                [attr.rel]="action.external ? 'noopener' : null"
                >{{ action.label }}</a
              >
            }
            @if (footer === 'client' || footer === 'preview') {
              <button hlmBtn variant="outline" type="button" class="w-full pointer-coarse:min-h-11" (click)="close()">
                Закрыть
              </button>
            }
          </div>

          @if (footer === 'preview') {
            <!-- Только телефон: шторка занимает весь экран, снаружи кнопке места нет. Вне блока
                 actions — это действие фотографа над визиткой, а не контакт клиента. -->
            <button hlmBtn variant="outline" type="button" class="pointer-coarse:min-h-11" (click)="close('change-design')">
              Сменить дизайн
            </button>
          }

          @if (footer === 'settings') {
            <!-- Единственная кнопка превью фотографа: она ведёт туда, где визитку и собирают.
                 Стоит вне блока actions, чтобы дизайны визитки не гасили её своими стилями. -->
            <button hlmBtn size="lg" type="button" class="w-full pointer-coarse:min-h-11" (click)="close('account-settings')">
              Настроить визитку
            </button>
          }
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class PhotographerCardComponent {
  // Появление: карточка всплывает целиком — тень и содержимое в одном кадре, на общих
  // dialog-утилитах. На телефоне хост — display:contents, шторка ведёт свою анимацию сама.
  protected readonly hostClass =
    dialogSheetHostClass('xs', 'p-8', 'public') +
    ' motion-safe:animate-in fade-in-0 zoom-in-98 fill-mode-both duration-[var(--t-mid)] ease-[var(--ease-smooth)]';

  private readonly dialogRef = inject(BrnDialogRef);

  // The opener hands the card through the dialog context, not through inputs:
  // Brn passes it as DIALOG_DATA, so a required input would never be set.
  private readonly context = injectBrnDialogContext<PhotographerCardContext>();
  protected readonly card = this.context.card;
  protected readonly footer: PhotographerCardFooter = this.context.footer ?? 'client';
  /** Хост-класс темы: без выбора визитка тёмная. */
  protected readonly theme: VisitkaTheme = this.card.theme ?? 'dark';
  /** Хост-класс дизайна: тот, что выбрал фотограф, иначе дизайн по умолчанию. */
  readonly design = input<VisitkaDesign>(this.card.design ?? DEFAULT_VISITKA_DESIGN);

  protected readonly initials = photographerInitials(this.card.name);
  /** Имя не заполнено — клиент читает «Фотограф»; превью фотографа само шлёт «Ваше имя». */
  protected readonly name = this.card.name?.trim() || 'Фотограф';
  protected readonly phone = this.card.phone?.trim() || null;

  /**
   * One button per filled contact, in the order the card reads: почта → сайт → соцсети →
   * мессенджеры (мессенджер по умолчанию первым — так его прислал открывающий).
   */
  protected readonly actions = [
    ...this.mailAction(),
    ...this.siteAction(),
    ...this.linkActions(false),
    ...this.linkActions(true),
  ];

  private mailAction(): { href: string; label: string; external: boolean }[] {
    const email = this.card.email?.trim();
    return email ? [{ href: `mailto:${email}`, label: `Написать на ${email}`, external: false }] : [];
  }

  private siteAction(): { href: string; label: string; external: boolean }[] {
    const site = this.card.siteUrl;
    return site ? [{ href: site, label: 'Сайт', external: true }] : [];
  }

  private linkActions(messengers: boolean): { href: string; label: string; external: boolean }[] {
    return this.card.links
      .filter(link => MESSENGERS.includes(link.channel) === messengers)
      .map(link => ({
        href: link.url,
        label: messengers ? `Написать в ${CHANNEL_NAMES[link.channel]}` : CHANNEL_NAMES[link.channel],
        external: true,
      }));
  }

  protected close(result?: PhotographerCardResult): void {
    this.dialogRef.close(result);
  }
}
